import { Box, Card, CardContent, Stack } from '@mui/material';
import { BrandMark } from '../layout/BrandMark';
import { LoginHeroPanel } from './LoginHeroPanel';
import { LoadingState } from '../common/LoadingState';
import { PageTransition } from '../common/PageTransition';
import { LoginCardTheme } from '../../theme/loginCardTheme';
import { alpha, tokens } from '../../theme/tokens';
import { usePrefersReducedMotion } from '../../motion/transitions';

export function AuthScreen({ hydrating, children }) {
  const reduceMotion = usePrefersReducedMotion();

  if (hydrating) {
    return (
      <Box sx={{ p: 3 }}>
        <LoadingState label="Opening Prasaar…" />
      </Box>
    );
  }

  const stageSx = (delayMs) => ({
    '@keyframes prasaarStageIn': {
      from: {
        opacity: 0,
        transform: reduceMotion ? 'none' : 'translateY(14px)',
      },
      to: { opacity: 1, transform: 'none' },
    },
    animation: `prasaarStageIn ${reduceMotion ? 160 : 560}ms cubic-bezier(0.16, 1, 0.3, 1) both`,
    animationDelay: reduceMotion ? '0ms' : `${delayMs}ms`,
  });

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        background: {
          xs: `
            radial-gradient(90% 55% at 50% 0%, ${alpha(tokens.brand, 0.28)}, transparent 55%),
            linear-gradient(180deg, ${tokens.shellDeep} 0%, ${tokens.shell} 55%, #1a222c 100%)
          `,
          md: `
            radial-gradient(55% 70% at 22% 45%, ${alpha(tokens.brand, 0.32)}, transparent 60%),
            radial-gradient(40% 45% at 85% 75%, ${alpha(tokens.brand, 0.06)}, transparent 55%),
            linear-gradient(105deg,
              ${tokens.shellDeep} 0%,
              ${tokens.shell} 42%,
              #161c26 68%,
              #1a222c 100%
            )
          `,
        },
      }}
    >
      <PageTransition>
        <Box
          sx={{
            minHeight: '100dvh',
            width: '100%',
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'minmax(0, 1.1fr) minmax(340px, 0.9fr)',
              lg: 'minmax(0, 1.15fr) minmax(400px, 0.85fr)',
            },
            gridTemplateRows: { xs: 'auto 1fr', md: '1fr' },
          }}
        >
          <Box sx={{ order: 1, minHeight: { xs: 0, md: '100dvh' }, p: { xs: 2, sm: 2.5, md: 0 } }}>
            <LoginHeroPanel />
          </Box>
          <Box
            sx={{
              order: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: { xs: 2, sm: 3, md: 4, lg: 6 },
              py: { xs: 2.5, sm: 3, md: 4 },
              minHeight: { xs: 0, md: '100dvh' },
            }}
          >
            <LoginCardTheme>
              <Card
                sx={{
                  width: '100%',
                  maxWidth: { xs: 440, md: 420, lg: 460 },
                  position: 'relative',
                  bgcolor: tokens.cardLight,
                  border: '1px solid',
                  borderColor: alpha('#FFFFFF', 0.12),
                  boxShadow: `0 28px 64px ${alpha(tokens.shellDeep, 0.45)}`,
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 3,
                    background: `linear-gradient(90deg, ${tokens.brand}, ${tokens.signal})`,
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 2.75, sm: 3.5, md: 4 } }}>
                  <Stack spacing={{ xs: 2, sm: 2.5 }}>
                    <Box sx={stageSx(0)}>
                      <BrandMark always />
                    </Box>
                    {typeof children === 'function' ? children(stageSx) : children}
                  </Stack>
                </CardContent>
              </Card>
            </LoginCardTheme>
          </Box>
        </Box>
      </PageTransition>
    </Box>
  );
}

export const authSubmitSx = {
  bgcolor: tokens.brandFill,
  color: '#FFFFFF',
  '&:hover': { bgcolor: tokens.brandInk, color: '#FFFFFF' },
  '&.Mui-disabled': { bgcolor: tokens.primarySoft, color: tokens.inkMuted },
};
